/**
 * @fileoverview Automations manager (MOR-736)
 *
 * The org's automation home, split into tabs to keep the kinds of automation
 * clearly separate:
 *
 *  - "General"   — org-wide rules that run on EVERY job. Created and
 *                  edited here.
 *  - "Workflow"  — rules built into workflow templates (they only run on
 *                  jobs their workflow is applied to). Shown here grouped
 *                  by workflow for visibility, with quick enable/disable;
 *                  editing happens in the owning workflow's editor, one
 *                  click away.
 *  - "Job"       — job-scoped rules attached to a single job's tasks (they
 *                  only run on that job). Shown here grouped by job with
 *                  inline edit/delete — this is their management surface
 *                  alongside the task side panel where they're created.
 *  - "Run history" — what every automation (all kinds) actually did.
 *
 * Admin/owner only — `canEdit` gates writes; the backend enforces the same
 * (job-scoped writes additionally require job write access).
 */

import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  ArrowUpRight,
  Briefcase,
  LayoutTemplate,
  Loader2,
  Lock,
  Pencil,
  Plus,
  Trash2,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { Badge, Button, Switch } from "@/ui/primitives";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/ui/composed/overlays/alert-dialog";
import { Tabs, TabsList, TabsTrigger } from "@/ui/composed/sections/tabs";
import { extractErrorMessage } from "@/utils/errors";
import {
  AutomationRuleDialog,
  type EditableRule,
} from "./AutomationRuleDialog";
import { AutomationRuleSummary } from "./AutomationRuleSummary";
import {
  ACTION_BADGE_COLOR,
  describeAction,
  eventLabel,
} from "./automation-config";
import { TriggerBadges } from "./TriggerBadges";

const GROUP_CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] transition-shadow duration-200 overflow-hidden";

type ManagerTab = "general" | "workflow" | "job" | "runs";

function formatDateTime(ms: number): string {
  return new Date(ms).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function AutomationsManager({
  orgId,
  canEdit,
}: {
  orgId: Id<"organizations">;
  canEdit: boolean;
}) {
  const navigate = useNavigate();
  const { data: rules, isPending } = useQuery(
    convexQuery(api.automations.rules.listByOrg, { orgId }),
  );
  const { data: runs } = useQuery(
    convexQuery(api.automations.rules.listRuns, { orgId }),
  );
  const { data: statuses } = useQuery(
    convexQuery(api.tasks.statuses.listByOrg, { orgId }),
  );
  const updateRule = useConvexMutation(api.automations.rules.update);
  const archiveRule = useConvexMutation(api.automations.rules.archive);

  const [tab, setTab] = useState<ManagerTab>("workflow");
  const [busy, setBusy] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<EditableRule | undefined>();
  const [pendingDelete, setPendingDelete] = useState<{
    id: Id<"automationRules">;
    name: string;
  } | null>(null);

  const generalRules = useMemo(
    () => (rules ?? []).filter((r) => !r.templateId && !r.jobId),
    [rules],
  );
  /** Workflow-scoped rules grouped by owning workflow, in name order. */
  const workflowGroups = useMemo(() => {
    const groups = new Map<
      string,
      {
        templateId: Id<"taskTemplates">;
        templateName: string;
        rules: NonNullable<typeof rules>;
      }
    >();
    for (const rule of rules ?? []) {
      if (!rule.templateId) continue;
      const existing = groups.get(rule.templateId);
      if (existing) existing.rules.push(rule);
      else {
        groups.set(rule.templateId, {
          templateId: rule.templateId,
          templateName: rule.templateName ?? "Untitled workflow",
          rules: [rule],
        });
      }
    }
    return [...groups.values()].sort((a, b) =>
      a.templateName.localeCompare(b.templateName),
    );
  }, [rules]);
  const workflowRuleCount = workflowGroups.reduce(
    (n, g) => n + g.rules.length,
    0,
  );
  /** Job-scoped rules grouped by owning job, in name order. */
  const jobGroups = useMemo(() => {
    const groups = new Map<
      string,
      {
        jobId: Id<"jobs">;
        jobName: string;
        rules: NonNullable<typeof rules>;
      }
    >();
    for (const rule of rules ?? []) {
      if (!rule.jobId) continue;
      const existing = groups.get(rule.jobId);
      if (existing) existing.rules.push(rule);
      else {
        groups.set(rule.jobId, {
          jobId: rule.jobId,
          jobName: rule.jobName ?? "Untitled job",
          rules: [rule],
        });
      }
    }
    return [...groups.values()].sort((a, b) =>
      a.jobName.localeCompare(b.jobName),
    );
  }, [rules]);
  const jobRuleCount = jobGroups.reduce((n, g) => n + g.rules.length, 0);

  const openNew = () => {
    setEditing(undefined);
    setEditorOpen(true);
  };
  const openEdit = (rule: NonNullable<typeof rules>[number]) => {
    setEditing({
      _id: rule._id,
      templateId: rule.templateId,
      templateName: rule.templateName,
      jobId: rule.jobId,
      jobName: rule.jobName,
      name: rule.name,
      description: rule.description,
      enabled: rule.enabled,
      trigger: rule.trigger,
      conditions: rule.conditions,
      actions: rule.actions,
    });
    setEditorOpen(true);
  };

  const openWorkflow = (templateId: Id<"taskTemplates">) =>
    navigate({
      to: "/dashboard/workflows/$workflowId",
      params: { workflowId: templateId },
      search: { tab: "automations" as const },
    });

  /**
   * Open the job's task board, deep-linking straight to a task by title when
   * the rule targets one (MOR-736). Job automations are about tasks, so we
   * land on the tasks board (not the key-dates calendar).
   */
  const openJobTask = (jobId: Id<"jobs">, taskTitle?: string) =>
    navigate({
      to: "/dashboard/jobs/$jobId/calendar/tasks",
      params: { jobId },
      search: taskTitle?.trim() ? { task: taskTitle.trim() } : {},
    });

  const handleToggle = async (
    ruleId: Id<"automationRules">,
    enabled: boolean,
  ) => {
    try {
      await updateRule({ ruleId, patch: { enabled } });
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not update the automation."));
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setBusy(true);
    try {
      await archiveRule({ ruleId: pendingDelete.id });
      toast.success("Automation deleted.");
      setPendingDelete(null);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not delete the automation."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex w-full flex-col gap-6">
      {!canEdit && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200/60 bg-amber-50 px-4 py-3 text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
          <Lock className="mt-0.5 size-4 shrink-0" />
          <div className="text-sm">
            <p className="font-medium">Read-only view</p>
            <p className="mt-0.5 text-xs opacity-80">
              You need owner or admin access to manage automations.
            </p>
          </div>
        </div>
      )}

      <div className={GROUP_CARD_CLASS}>
        <div className="flex items-start justify-between gap-6 border-b border-border p-6">
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold text-primary">Automations</h3>
            <p className="mt-1 text-sm text-primary/60">
              Create and manage automations, which are event based triggers that
              you can apply (e.g. change assignee when task has been updated to
              in review). Automations can be used to update tasks, notify users,
              start reminders, and trigger actions (e.g. searches).
            </p>
          </div>
          {canEdit && tab === "general" && (
            <Button size="sm" onClick={openNew} disabled={busy}>
              <Plus className="mr-1.5 size-4" />
              New general automation
            </Button>
          )}
        </div>

        <div className="border-b border-border px-6 pt-4">
          <Tabs value={tab} onValueChange={(v) => setTab(v as ManagerTab)}>
            <TabsList>
              <TabsTrigger value="workflow">
                <LayoutTemplate className="mr-1.5 size-3.5" />
                Workflow automations ({workflowRuleCount})
              </TabsTrigger>
              <TabsTrigger value="job">
                <Briefcase className="mr-1.5 size-3.5" />
                Job automations ({jobRuleCount})
              </TabsTrigger>
              <TabsTrigger value="general">
                General ({generalRules.length})
              </TabsTrigger>
              <TabsTrigger value="runs">Run history</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* ------------------------------------------------------------- */}
        {/* General — org-wide rules, created and edited right here.       */}
        {/* ------------------------------------------------------------- */}
        {tab === "general" &&
          (isPending ? (
            <div className="flex items-center justify-center gap-2 px-6 py-16 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Loading automations…
            </div>
          ) : generalRules.length === 0 ? (
            <div className="px-6 py-16 text-center text-sm text-muted-foreground">
              <Zap className="mx-auto mb-2 size-6 opacity-40" />
              <p className="font-medium text-foreground/80">
                No general automations yet.
              </p>
              <p className="mx-auto mt-1 max-w-md">
                General automations run on <strong>every job</strong> and are
                not tied to specific tasks or workflows (e.g. when KYC search is
                complete, notify appointee).
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>When</TableHead>
                  <TableHead>Then</TableHead>
                  <TableHead className="w-[90px] text-center">Runs</TableHead>
                  <TableHead className="w-[90px] text-center">
                    Enabled
                  </TableHead>
                  <TableHead className="w-[110px] text-right">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {generalRules.map((rule) => (
                  <TableRow
                    key={rule._id}
                    className={canEdit ? "cursor-pointer" : undefined}
                    onClick={() => canEdit && openEdit(rule)}
                  >
                    <TableCell className="font-medium text-primary">
                      {rule.name}
                      {rule.description && (
                        <p className="mt-0.5 max-w-xs truncate text-xs font-normal text-muted-foreground">
                          {rule.description}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <TriggerBadges
                        trigger={rule.trigger}
                        statuses={statuses}
                      />
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <div className="flex flex-wrap gap-1">
                        {rule.actions.slice(0, 3).map((a, i) => (
                          <Badge
                            key={i}
                            variant="soft"
                            color={ACTION_BADGE_COLOR[a.type]}
                            className="font-normal"
                          >
                            {describeAction(a)}
                          </Badge>
                        ))}
                        {rule.actions.length > 3 && (
                          <Badge variant="outline" className="font-normal">
                            +{rule.actions.length - 3} more
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center text-muted-foreground">
                      {rule.runCount}
                    </TableCell>
                    <TableCell
                      className="text-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Switch
                        checked={rule.enabled}
                        disabled={!canEdit}
                        onCheckedChange={(v) => void handleToggle(rule._id, v)}
                      />
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {canEdit && (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Edit"
                              onClick={() => openEdit(rule)}
                            >
                              <Pencil className="size-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Delete"
                              onClick={() =>
                                setPendingDelete({
                                  id: rule._id,
                                  name: rule.name,
                                })
                              }
                            >
                              <Trash2 className="size-4 text-destructive" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ))}

        {/* ------------------------------------------------------------- */}
        {/* Workflow automations — grouped read view; managed in the        */}
        {/* owning workflow's editor (one click away).                      */}
        {/* ------------------------------------------------------------- */}
        {tab === "workflow" && (
          <div className="space-y-5 p-6">
            <p className="text-sm text-muted-foreground">
              These rules are built into a workflow and only run on jobs that
              workflow has been applied to. They&apos;re managed inside the
              workflow itself — this is a read view with quick enable/disable.
            </p>
            {workflowGroups.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border px-6 py-12 text-center text-sm text-muted-foreground">
                <LayoutTemplate className="mx-auto mb-2 size-6 opacity-40" />
                No workflow automations yet. Open a workflow and use its
                Automations tab to add rules that follow the workflow onto every
                job it&apos;s applied to.
              </div>
            ) : (
              workflowGroups.map((group) => (
                <div
                  key={group.templateId}
                  className="overflow-hidden rounded-lg border border-border"
                >
                  <div className="flex items-center justify-between gap-3 border-b border-border bg-muted/40 px-4 py-2.5">
                    <p className="flex items-center gap-1.5 text-sm font-semibold text-primary">
                      <LayoutTemplate className="size-4" />
                      {group.templateName}
                      <span className="font-normal text-muted-foreground">
                        · {group.rules.length} automation
                        {group.rules.length === 1 ? "" : "s"}
                      </span>
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 gap-1 text-xs"
                      onClick={() => void openWorkflow(group.templateId)}
                    >
                      {canEdit ? "Edit in workflow" : "View workflow"}
                      <ArrowUpRight className="size-3.5" />
                    </Button>
                  </div>
                  <div className="divide-y divide-border">
                    {group.rules.map((rule) => (
                      <div
                        key={rule._id}
                        className="flex items-start justify-between gap-3 px-4 py-3"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-primary">
                            {rule.name}
                          </p>
                          <AutomationRuleSummary
                            orgId={orgId}
                            trigger={rule.trigger}
                            actions={rule.actions}
                            className="mt-1"
                          />
                        </div>
                        <div className="flex shrink-0 items-center gap-3">
                          <span className="text-xs tabular-nums text-muted-foreground">
                            {rule.runCount} run{rule.runCount === 1 ? "" : "s"}
                          </span>
                          <Switch
                            checked={rule.enabled}
                            disabled={!canEdit}
                            onCheckedChange={(v) =>
                              void handleToggle(rule._id, v)
                            }
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* ------------------------------------------------------------- */}
        {/* Job automations — grouped read view with inline edit/delete.   */}
        {/* These are created from a job task's side panel and only run on  */}
        {/* that job.                                                       */}
        {/* ------------------------------------------------------------- */}
        {tab === "job" && (
          <div className="space-y-5 p-6">
            <p className="text-sm text-muted-foreground">
              These rules are attached to a single job&apos;s tasks and only run
              on that job. They&apos;re created from a task&apos;s side panel;
              edit or delete them here or from the job.
            </p>
            {jobGroups.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border px-6 py-12 text-center text-sm text-muted-foreground">
                <Briefcase className="mx-auto mb-2 size-6 opacity-40" />
                No job automations yet. Open a job task and use its Automations
                section to attach a rule that runs only on that job.
              </div>
            ) : (
              jobGroups.map((group) => (
                <div
                  key={group.jobId}
                  className="overflow-hidden rounded-lg border border-border"
                >
                  <div className="flex items-center justify-between gap-3 border-b border-border bg-muted/40 px-4 py-2.5">
                    <p className="flex items-center gap-1.5 text-sm font-semibold text-primary">
                      <Briefcase className="size-4" />
                      {group.jobName}
                      <span className="font-normal text-muted-foreground">
                        · {group.rules.length} automation
                        {group.rules.length === 1 ? "" : "s"}
                      </span>
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 gap-1 text-xs"
                      onClick={() =>
                        void openJobTask(
                          group.jobId,
                          group.rules[0]?.trigger.taskTitleContains,
                        )
                      }
                    >
                      Open job
                      <ArrowUpRight className="size-3.5" />
                    </Button>
                  </div>
                  <div className="divide-y divide-border">
                    {group.rules.map((rule) => (
                      <div
                        key={rule._id}
                        className="flex items-start justify-between gap-3 px-4 py-3"
                      >
                        <div className="min-w-0 flex-1">
                          {rule.trigger.taskTitleContains ? (
                            <button
                              type="button"
                              onClick={() =>
                                void openJobTask(
                                  group.jobId,
                                  rule.trigger.taskTitleContains,
                                )
                              }
                              title="Open this task in the job"
                              className="text-left text-sm font-medium text-primary underline-offset-2 hover:underline"
                            >
                              {rule.name}
                            </button>
                          ) : (
                            <p className="text-sm font-medium text-primary">
                              {rule.name}
                            </p>
                          )}
                          <AutomationRuleSummary
                            orgId={orgId}
                            trigger={rule.trigger}
                            actions={rule.actions}
                            className="mt-1"
                          />
                        </div>
                        <div className="flex shrink-0 items-center gap-3">
                          <span className="text-xs tabular-nums text-muted-foreground">
                            {rule.runCount} run{rule.runCount === 1 ? "" : "s"}
                          </span>
                          <Switch
                            checked={rule.enabled}
                            disabled={!canEdit}
                            onCheckedChange={(v) =>
                              void handleToggle(rule._id, v)
                            }
                          />
                          {canEdit && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Edit"
                                onClick={() => openEdit(rule)}
                              >
                                <Pencil className="size-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Delete"
                                onClick={() =>
                                  setPendingDelete({
                                    id: rule._id,
                                    name: rule.name,
                                  })
                                }
                              >
                                <Trash2 className="size-4 text-destructive" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* ------------------------------------------------------------- */}
        {/* Run history — all kinds, newest first.                         */}
        {/* ------------------------------------------------------------- */}
        {tab === "runs" &&
          (!runs || runs.length === 0 ? (
            <div className="px-6 py-16 text-center text-sm text-muted-foreground">
              No runs yet — runs appear here once an automation fires.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Automation</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Job</TableHead>
                  <TableHead>Outcome</TableHead>
                  <TableHead className="w-[140px]">When</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.map((run) => (
                  <TableRow key={run._id}>
                    <TableCell className="font-medium text-primary">
                      {run.ruleName}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {eventLabel(run.event)}
                    </TableCell>
                    <TableCell className="max-w-[180px] truncate text-muted-foreground">
                      {run.jobName}
                    </TableCell>
                    <TableCell className="max-w-sm">
                      <div className="flex items-center gap-2">
                        <Badge
                          variant={
                            run.status === "success"
                              ? "default"
                              : run.status === "failed"
                                ? "destructive"
                                : "secondary"
                          }
                        >
                          {run.status}
                        </Badge>
                        {run.detail && (
                          <span
                            className="truncate text-xs text-muted-foreground"
                            title={run.detail}
                          >
                            {run.detail}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDateTime(run.createdAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ))}
      </div>

      {editorOpen && (
        <AutomationRuleDialog
          key={editing?._id ?? "new"}
          orgId={orgId}
          open={editorOpen}
          onOpenChange={setEditorOpen}
          initial={editing}
        />
      )}

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this automation?</AlertDialogTitle>
            <AlertDialogDescription>
              “{pendingDelete?.name}” will stop running. Its run history is
              kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleDelete();
              }}
              disabled={busy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {busy ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
